import type { Row } from '@/ui/table/table.types';

import type { SkeletonRowConfig } from './skeleton-row';
import { SkeletonRow } from './skeleton-row';
import { TableRow } from './table-row';
import { styled } from 'goober';

export const INFINITE_TABLE_ROW_HEIGHT = 76;

export interface TableSkeletonConfig extends SkeletonRowConfig {
  numberOfRows?: number;
}

interface Props {
  id?: string;
  rows: Row[];
  skeletonConfig?: TableSkeletonConfig;
  isLoading?: boolean;
  borderColor?: string;
  rowCursor?: string;
  className?: string;
  shadow?: boolean;
}

export const TableBody = ({
  id,
  rows,
  isLoading,
  skeletonConfig,
  borderColor,
  rowCursor,
  className,
  shadow,
}: Props) => {
  const generateSkeletonRows = (skeletonConfig: TableSkeletonConfig) => {
    if (skeletonConfig.numberOfRows) {
      return (
        <>
          {Array.from({ length: skeletonConfig.numberOfRows }).map(
            (_, index) => (
              <SkeletonRow
                key={index}
                {...skeletonConfig}
                withRandomFirstColumnWidth
              />
            ),
          )}
        </>
      );
    }

    return <SkeletonRow {...skeletonConfig} />;
  };

  return (
    <TBody id={id} data-testid={id} className={className} shadow={shadow}>
      {isLoading && skeletonConfig
        ? generateSkeletonRows(skeletonConfig)
        : rows.map((row, index) => (
            <TableRow
              id={id ? `${id}-row-${row.id}` : undefined}
              key={row.id}
              row={row}
              borderColor={borderColor}
              bgColor={row.rowBackgroundColor}
              bgColorHover={row.rowBackgroundHover}
              rowCursor={rowCursor}
              isLast={index >= rows.length - 1}
            />
          ))}
    </TBody>
  );
};

const TBody = styled('tbody')<{ shadow?: boolean }>`
  ${({ shadow, theme }) => shadow && ` box-shadow: ${theme.shadow.table}`};
`;
